<template>
  <div class="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 py-12">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Balance Alert Banner (only shown when balance is 0) -->
      <div
        v-if="!userProfile.mandala_drawings?.remaining || userProfile.mandala_drawings.remaining <= 0"
        class="mb-8 transform transition-all duration-300 hover:scale-105"
      >
        <div class="relative overflow-hidden rounded-xl bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 p-[2px]">
          <div class="relative rounded-xl bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600">
            <div class="px-6 py-8">
              <div class="flex flex-col md:flex-row items-center justify-between gap-6">
                <div class="flex items-center space-x-4">
                  <div class="flex h-12 w-12 items-center justify-center rounded-full bg-white/10">
                    <span class="text-2xl">⚡️</span>
                  </div>
                  <div>
                    <h3 class="text-xl font-bold text-white">
                      {{ $t('message.unlockAllFeatures') }}
                    </h3>
                    <p class="text-indigo-200">
                      {{ $t('message.limitedFeatures') }} ✨
                    </p>
                  </div>
                </div>
                <router-link
                  to="/payment"
                  class="group relative overflow-hidden rounded-lg bg-white px-6 py-3 text-indigo-600 shadow-md transition-all duration-300 hover:shadow-lg hover:scale-105"
                >
                  <div class="absolute inset-0 bg-gradient-to-r from-violet-400 to-indigo-400 opacity-0 transition-opacity group-hover:opacity-10" />
                  <span class="flex items-center font-semibold">
                    {{ $t('message.upgradeNow') }} 🚀
                  </span>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Profile Card with Loading and Error States -->
      <div v-if="isLoading" class="bg-white rounded-2xl shadow-xl p-8 flex justify-center items-center min-h-[200px]">
        <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>

      <div v-else-if="errors.length > 0" class="bg-white rounded-2xl shadow-xl p-8">
        <div class="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg" role="alert">
          <strong class="font-bold">{{ $t('message.errorTitle') }}</strong>
          <ul class="mt-1">
            <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
          </ul>
        </div>
      </div>

      <!-- Main Profile Content -->
      <div v-else class="space-y-6">
        <!-- User Header Card -->
        <div class="bg-white rounded-2xl shadow-md overflow-hidden">
          <div class="p-6 bg-gradient-to-r from-indigo-500/5 via-purple-500/5 to-pink-500/5">
            <div class="flex flex-col sm:flex-row items-center gap-6">
              <div class="relative group">
                <div class="absolute -inset-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full opacity-50 group-hover:opacity-100 blur transition duration-300"></div>
                <img
                  :src="userProfile.avatar || defaultAvatar"
                  alt=""
                  class="relative h-24 w-24 rounded-full object-cover border-4 border-white shadow-lg"
                />
              </div>
              <div>
                <h1 class="text-2xl font-bold text-gray-900">
                  {{ userProfile.first_name || userProfile.username }} ✌️
                </h1>
                <p class="text-sm text-gray-500">
                  {{ $t('message.joinedCaption') }} {{ formatDate(userProfile.joined_date) }} 🎉
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Drawing Balance Card (Only shown if balance > 0) -->
        <div v-if="userProfile.mandala_drawings?.remaining > 0" class="bg-white rounded-2xl shadow-md overflow-hidden">
          <div class="p-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              Баланс рисунков 🎨
            </h2>
            <div class="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-gray-600">Доступно для создания</p>
                  <p class="text-3xl font-bold text-gray-900 mt-1">
                    {{ userProfile.mandala_drawings.remaining }}
                  </p>
                </div>
                <router-link
                  to="/app"
                  class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Рисовать
                </router-link>
                  <router-link
                    to="/payment"
                    class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    {{ $t('message.buyMore') }}
                  </router-link>
              </div>
            </div>
          </div>
        </div>

        <!-- NEWLY ADDED: Purchased Learning Sets Card -->
        <div v-if="hasPurchasedLearningSets" class="bg-white rounded-2xl shadow-md overflow-hidden">
          <div class="p-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              {{ $t('message.purchasedLearningSets') }} 📚
            </h2>

            <div class="space-y-4">
              <div v-for="learningSet in userProfile.purchased_learning_sets" :key="learningSet.id"
                   class="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100 transition-all duration-300 hover:shadow-md">
                <div class="flex items-start gap-4">
                  <div class="flex-shrink-0">
                    <div class="h-12 w-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                      <img v-if="learningSet.icon_url" :src="learningSet.icon_url" :alt="learningSet.name" class="h-10 w-10 object-cover rounded">
                      <span v-else class="text-2xl">📘</span>
                    </div>
                  </div>
                  <div class="flex-grow">
                    <h3 class="font-medium text-gray-900 text-sm md:text-base">{{ learningSet.name }}</h3>
                    <p class="text-xs text-gray-500 mt-1">{{ learningSet.topic_name }}</p>
                    <div class="flex items-center justify-between mt-3">
                      <span class="text-xs text-gray-500">{{ $t('message.purchased') }}: {{ formatDate(learningSet.purchased_at) }}</span>
                      <a href="#" class="text-sm text-indigo-600 font-medium hover:text-indigo-800 flex items-center gap-1">
                        {{ $t('message.download') }}
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Profile Information Card -->
        <div class="bg-white rounded-2xl shadow-md overflow-hidden">
          <div class="p-6 bg-gradient-to-r from-purple-50/30 via-transparent to-transparent">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h2 class="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  {{ $t('message.userProfileCaption') }} 👤
                </h2>
                <dl class="mt-4 space-y-4">
                  <div>
                    <dt class="text-sm font-medium text-gray-500">{{ $t('message.usernameCaption') }}</dt>
                    <dd class="mt-1 text-sm text-gray-900">{{ userProfile.username }}</dd>
                  </div>
                  <div>
                    <dt class="text-sm font-medium text-gray-500">{{ $t('message.emailCaption') }}</dt>
                    <dd class="mt-1 text-sm text-gray-900">{{ userProfile.email }}</dd>
                  </div>
                  <div>
                    <dt class="text-sm font-medium text-gray-500">{{ $t('message.locationCaption') }}</dt>
                    <dd class="mt-1 text-sm text-gray-900">{{ userProfile.location || $t('message.notSpecified') }}</dd>
                  </div>
                </dl>
              </div>
              <div>
                <h2 class="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  {{ $t('message.bioCaption') }} 📝
                </h2>
                <p class="mt-4 text-sm text-gray-600">
                  {{ userProfile.bio || $t('message.noBio') }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Password Change Card -->
        <div class="bg-white rounded-2xl shadow-md overflow-hidden">
          <div class="p-6">
            <button
              @click="showPasswordForm = !showPasswordForm"
              class="w-full flex items-center justify-between text-lg font-semibold text-gray-900"
            >
              <span class="flex items-center gap-2">
                {{ $t('message.changePassword') }}
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </span>
              <svg
                :class="{ 'rotate-180': showPasswordForm }"
                class="h-5 w-5 text-gray-400 transition-transform duration-200"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <div v-if="showPasswordForm" class="mt-6 space-y-4">
              <!-- Success Message -->
              <div v-if="passwordSuccess" class="p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                {{ $t('message.passwordChangedSuccess') }}
              </div>

              <!-- Error Message -->
              <div v-if="passwordError" class="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                {{ passwordError }}
              </div>

              <form @submit.prevent="changePassword" class="space-y-4">
                <div>
                  <label for="currentPassword" class="block text-sm font-medium text-gray-700">
                    {{ $t('message.currentPassword') }}
                  </label>
                  <input
                    type="password"
                    id="currentPassword"
                    v-model="passwordForm.currentPassword"
                    :class="{ 'border-red-500': passwordErrors.currentPassword }"
                    class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    required
                  />
                  <p v-if="passwordErrors.currentPassword" class="mt-1 text-sm text-red-600">
                    {{ passwordErrors.currentPassword }}
                  </p>
                </div>

                <div>
                  <label for="newPassword" class="block text-sm font-medium text-gray-700">
                    {{ $t('message.newPassword') }}
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    v-model="passwordForm.newPassword"
                    :class="{ 'border-red-500': passwordErrors.newPassword }"
                    class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    required
                    minlength="6"
                  />
                  <p v-if="passwordErrors.newPassword" class="mt-1 text-sm text-red-600">
                    {{ passwordErrors.newPassword }}
                  </p>
                  <p class="mt-1 text-xs text-gray-500">{{ $t('message.passwordMinLength') }}</p>
                </div>

                <div>
                  <label for="confirmPassword" class="block text-sm font-medium text-gray-700">
                    {{ $t('message.confirmPassword') }}
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    v-model="passwordForm.confirmPassword"
                    :class="{ 'border-red-500': passwordErrors.confirmPassword }"
                    class="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    required
                  />
                  <p v-if="passwordErrors.confirmPassword" class="mt-1 text-sm text-red-600">
                    {{ passwordErrors.confirmPassword }}
                  </p>
                </div>

                <div class="flex justify-end">
                  <button
                    type="submit"
                    :disabled="isChangingPassword"
                    class="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span v-if="isChangingPassword" class="flex items-center">
                      <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {{ $t('message.saving') }}
                    </span>
                    <span v-else>{{ $t('message.savePassword') }}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useAuthStore } from '@/stores/user'
import { useI18n } from 'vue-i18n'
import axiosInstance from '@/axios/axios'
import defaultAvatar from '@/assets/images/person-3.png'

export default {
  name: 'UserProfile',
  setup() {
    const { t } = useI18n()
    const userStore = useAuthStore()
    const isLoading = ref(true)
    const errors = ref([])
    const userProfile = ref({
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      bio: '',
      location: '',
      joined_date: '',
      avatar: '',
      subscription_status: null,
      subscription_details: null,
      mandala_drawings: {
        allowed: 0,
        used: 0,
        remaining: 0,
        reset_date: null
      },
      purchased_learning_sets: []
    })

    // Password change state
    const showPasswordForm = ref(false)
    const isChangingPassword = ref(false)
    const passwordSuccess = ref(false)
    const passwordError = ref('')
    const passwordForm = reactive({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    })
    const passwordErrors = reactive({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    })

    // Check if user has purchased learning sets
    const hasPurchasedLearningSets = computed(() => {
      return userProfile.value.purchased_learning_sets &&
             userProfile.value.purchased_learning_sets.length > 0
    })

    const formatDate = (date) => {
      if (!date) return ''
      return new Date(date).toLocaleDateString()
    }

    const fetchUserProfile = async () => {
      try {
        isLoading.value = true
        const response = await axiosInstance.get('/api/auth/userinfo/')
        userProfile.value = response.data
        userStore.setUser(response.data)
      } catch (error) {
        console.error('Error fetching user profile:', error)
        errors.value = [error.response?.data?.detail || 'Failed to load user profile']
      } finally {
        isLoading.value = false
      }
    }

    const resetPasswordForm = () => {
      passwordForm.currentPassword = ''
      passwordForm.newPassword = ''
      passwordForm.confirmPassword = ''
      passwordErrors.currentPassword = ''
      passwordErrors.newPassword = ''
      passwordErrors.confirmPassword = ''
      passwordError.value = ''
    }

    const changePassword = async () => {
      // Reset errors
      passwordErrors.currentPassword = ''
      passwordErrors.newPassword = ''
      passwordErrors.confirmPassword = ''
      passwordError.value = ''
      passwordSuccess.value = false

      // Validate passwords match
      if (passwordForm.newPassword !== passwordForm.confirmPassword) {
        passwordErrors.confirmPassword = t('message.passwordsDoNotMatch')
        return
      }

      // Validate min length
      if (passwordForm.newPassword.length < 6) {
        passwordErrors.newPassword = t('message.passwordTooShort')
        return
      }

      try {
        isChangingPassword.value = true
        await axiosInstance.post('/api/auth/change-password/', {
          current_password: passwordForm.currentPassword,
          new_password: passwordForm.newPassword,
          confirm_password: passwordForm.confirmPassword
        })
        passwordSuccess.value = true
        resetPasswordForm()
      } catch (error) {
        console.error('Error changing password:', error)
        const data = error.response?.data
        if (data?.current_password) {
          passwordErrors.currentPassword = Array.isArray(data.current_password)
            ? data.current_password[0]
            : data.current_password
        } else if (data?.new_password) {
          passwordErrors.newPassword = Array.isArray(data.new_password)
            ? data.new_password[0]
            : data.new_password
        } else if (data?.confirm_password) {
          passwordErrors.confirmPassword = Array.isArray(data.confirm_password)
            ? data.confirm_password[0]
            : data.confirm_password
        } else {
          passwordError.value = data?.detail || t('message.passwordChangeError')
        }
      } finally {
        isChangingPassword.value = false
      }
    }

    onMounted(() => {
      fetchUserProfile()
    })

    return {
      isLoading,
      errors,
      userProfile,
      hasPurchasedLearningSets,
      formatDate,
      defaultAvatar,
      // Password change
      showPasswordForm,
      isChangingPassword,
      passwordSuccess,
      passwordError,
      passwordForm,
      passwordErrors,
      changePassword
    }
  }
}
</script>
